import {
  dansLesLimites,
  CATEGORIE_DE_REPLI,
  EVENT_CATEGORY_COLORS,
  EVENT_CATEGORY_LABELS,
  type LimitesDuSpot,
  type CreateEventInput,
  type EventCategory,
  type LangueParlee,
  type ParticipationStatus,
  type PublicUser,
  type Absence,
  type OrganizerRating,
  type ReportReason,
  type Sanction,
  type SiroccoEvent,
} from '@sirocco/shared';
import { LANGUES_MAX_PAR_SORTIE, sansAccents, versCarte, versListe, type CarteDeLangues } from '../utils/langues';
import { normaliserLeLien } from '../utils/lienEnLigne';
import { debutDe, estPasse, finDe, fuseauDe } from '../utils/spotTime';
import { requireUid } from './auth';
import { listUnseen } from './blocks';
import { uploadIfDataUrl } from './media';
import { paths } from './paths';
import { keysOf, loadUsers, unknownUser, type UserRecord } from './records';
import {
  noterArriveeDansLeGroupeDeSortie,
  noterSortieDuGroupeDeSortie,
} from './messages';
import {
  newKey,
  readKeys,
  readList,
  readOptionalKeys,
  readOptionalList,
  readOptionalValue,
  readValue,
  removeValue,
  SiroccoDataError,
  updateValues,
  writeValue,
} from './rtdb';
import { t } from '../i18n';

/**
 * Evenements.
 *
 * La base ne stocke qu'un squelette : des identifiants, une date et des
 * horaires. L'evenement affiche a l'ecran, lui, porte des profils
 * complets, la liste des participants, mon statut et mon signet. Cette
 * composition se fait ici, a la lecture, et toujours en parallele :
 * une liste de vingt cartes ne doit jamais devenir vingt allers-retours
 * en file indienne.
 */

/**
 * La categorie d'une sortie : la vraie si elle en a une neuve, sinon celle
 * d'origine. Une valeur inconnue — d'une version plus recente que celle-ci —
 * retombe sur une categorie connue plutot que de casser l'affichage.
 */
function categorieDe(record: { category?: string; categorie?: string }): EventCategory {
  const connue = (valeur?: string): valeur is EventCategory =>
    Boolean(valeur) && (valeur as string) in EVENT_CATEGORY_COLORS;
  if (connue(record.categorie)) return record.categorie;
  if (connue(record.category)) return record.category;
  return 'culture';
}

/** Un evenement, tel qu'il est ecrit dans `events/{serverId}/{eventId}`. */
export interface EventRecord {
  /** Masque par la moderation : le drapeau, jamais la suppression. */
  masque?: boolean;
  /** L'affiche, si l'organisateur en a mis une. */
  imageUrl?: string;
  /** A partir de combien de participants la sortie se confirme. */
  seuil?: number;
  /** Sortie spontanee : quoi, ou, quand, et rien d'autre. */
  spontane?: boolean;
  /**
   * En ligne : un lien a la place d'une adresse. `location` porte alors
   * « En ligne », dans la langue de l'organisateur, pour les versions
   * d'avant qui ne lisent que lui.
   */
  enLigne?: boolean;
  lien?: string;
  /** L'association qui l'organise : les regles exigent que l'organisateur en soit membre. */
  asso?: string;
  title: string;
  /** Une des quatre d'origine, toujours : voir `CATEGORIE_DE_REPLI`. */
  category: EventCategory;
  /** La vraie categorie, quand elle est neuve : les anciennes versions ne la lisent pas. */
  categorie?: EventCategory;
  subCategory: string;
  organizerId: string;
  organizerEventCount: number;
  /** Jour de l'evenement, au format "AAAA-MM-JJ". */
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  /** Le point de l'adresse, quand elle vient de l'annuaire. */
  lat?: number | null;
  lon?: number | null;
  description: string;
  /** Les langues qu'on y parlera. Absent sur les sorties d'avant : francais. */
  langues?: CarteDeLangues;
  maxParticipants: number;
  requiresValidation: boolean;
  createdAt: number;
  /** Conversation de groupe ouverte pour cet evenement, une fois seulement. */
  groupId?: string | null;
}

/** `eventParticipants/{eventId}` : qui est inscrit, et sous quel statut. */
type Roster = Record<string, ParticipationStatus> | null;

/** Un evenement brut et le spot ou il est range, avant composition. */
interface EventSource {
  id: string;
  serverId: string;
  record: EventRecord;
}

/**
 * Pourquoi un evenement passe ne compte pas pour les trophees.
 * Un evenement vecu seul n'a pas besoin de raison : il ne figure meme
 * pas dans la liste des passes.
 */
export type TrophySkip = 'same-day';

/** Ce que l'ecran "Mes evenements" recoit, agenda et comptage compris. */
export interface MyEvents {
  joined: SiroccoEvent[];
  organized: SiroccoEvent[];
  past: SiroccoEvent[];
  pastCount: number;
  /**
   * Mes evenements passes dont la feuille de presence n'est pas arretee.
   *
   * Ils ne figurent nulle part ailleurs : « Organises » ne montre que ce
   * qui est a venir, et « Passes » ne retient que ce ou l'on est alle.
   * Un evenement termine et jamais pointe disparaissait donc de la vue
   * de son organisateur — avec les absences qu'il n'a pas constatees.
   */
  sheetsToClose: SiroccoEvent[];
  /** Evenements passes qui comptent reellement pour les trophees. */
  trophyEventCount: number;
  /** Les autres, avec la raison, pour que l'ecart soit explicable. */
  trophySkipped: Record<string, TrophySkip>;
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MIN_PARTICIPANTS = 2;
const MAX_PARTICIPANTS = 500;

/* --- Lectures ------------------------------------------------------- */

/**
 * Le feed d'un spot, evenements a venir uniquement.
 * La Realtime Database ne sait pas chercher dans du texte : le filtre se
 * fait en memoire, sur une liste deja restreinte a une ville.
 */
export async function listEvents(serverId: string, search?: string): Promise<SiroccoEvent[]> {
  const uid = requireUid();
  const [rows, hidden, blocked] = await Promise.all([
    readList<EventRecord>(paths.events(serverId)),
    readOptionalKeys(paths.hiddenEvents(uid)),
    /* Bloques et masques : ni les uns ni les autres ne paraissent ici. */
    listUnseen(),
  ]);
  const needle = fold(search ?? '');
  const masked = new Set(hidden);
  const muted = new Set(blocked);

  const sources = rows
    .filter(
      (row) =>
        !row.masque &&
        !isPast(row, serverId) &&
        !masked.has(row.id) &&
        // Bloquer ou masquer quelqu'un, c'est aussi ne plus voir ce qu'il organise.
        !muted.has(row.organizerId) &&
        matches(row, needle),
    )
    .map((row) => ({ id: row.id, serverId, record: row }));

  const events = await composeMany(sources, uid);
  return events.sort(byDateAsc);
}

/**
 * Une fiche evenement.
 *
 * Le spot fourni sert de premiere piste ; sans lui, ou s'il se trompe —
 * un evenement joint a un message peut venir d'une autre ville —
 * `eventIndex` donne la reponse en une lecture, et le balayage complet
 * des spots ne sert plus que de filet pour les evenements anterieurs a
 * cet index.
 */
export async function getEvent(eventId: string, serverId?: string): Promise<SiroccoEvent> {
  const uid = requireUid();
  const source =
    (serverId ? await readSource(serverId, eventId) : null) ?? (await findAnywhere([eventId]))[0];
  if (!source) throw new SiroccoDataError(t.sorties.erreurs.introuvable, 'not-found');

  const [event] = await composeMany([source], uid);
  return event;
}

/**
 * Mes evenements, ranges comme l'ecran les affiche.
 *
 * Il n'existe pas d'index "mes evenements" : on balaye les feeds des
 * spots que je frequente, puis on ne garde que ceux ou j'apparais. Le
 * profil sert donc de filtre, faute de mieux.
 */
export async function listMine(): Promise<MyEvents> {
  const uid = requireUid();
  const [serverIds, hidden] = await Promise.all([
    myServerIds(uid),
    readOptionalKeys(paths.hiddenEvents(uid)),
  ]);
  const masked = new Set(hidden);

  /* Un serveur prive dont on n'a pas la cle ne rend rien : son refus ne
     doit pas emporter « Mes sorties ». */
  const feeds = await Promise.all(
    serverIds.map((id) => readOptionalList<EventRecord>(paths.events(id))),
  );
  const candidates: EventSource[] = feeds.flatMap((rows, index) =>
    rows
      // Un evenement masque depuis "Mes evenements" quitte toutes mes vues.
      .filter((row) => !masked.has(row.id))
      .map((row) => ({ id: row.id, serverId: serverIds[index], record: row })),
  );

  // Les participations sont lues une fois pour toutes ici : elles servent
  // d'abord a filtrer, puis a composer, sans seconde passe sur la base.
  const rosters = await readRosters(candidates);
  const mine = candidates
    .map((source, index) => ({ source, roster: rosters[index] }))
    .filter(({ source, roster }) => source.record.organizerId === uid || Boolean(roster?.[uid]));

  const events = await compose(
    mine.map((entry) => entry.source),
    mine.map((entry) => entry.roster),
    uid,
  );

  const upcoming = events.filter((event) => !isPast(event, event.serverId));
  /*
    S'inscrire n'est pas venir.

    « Mes evenements passes » ne montre que ceux ou l'organisateur a
    valide la presence — code lu ou case cochee. S'y etre inscrit sans y
    aller ne laisse donc aucune trace ici : la liste raconte ce qu'on a
    vecu, pas ce qu'on avait prevu.

    La presence n'est lue que pour les evenements passes, les seuls que
    cela concerne, et jamais pour une liste a venir. Et un evenement vecu
    seul n'entre pas non plus : `participants` ne porte que les confirmes,
    donc moins de deux, c'est moi tout seul.
  */
  const revolus = events
    .filter((event) => isPast(event, event.serverId) && event.participants.length >= 2)
    .sort(byDateDesc);
  const presences = await Promise.all(revolus.map((event) => readAttendance(event.id)));
  const past = revolus.filter((_, index) => presences[index][uid]);
  const ledger = trophyLedger(past);

  /*
    Les feuilles qui attendent.

    Seulement celles ou quelqu'un d'autre s'etait engage : arreter la
    feuille d'un evenement ou l'on etait seul ne constate rien et ne
    penalise personne, le rappeler serait du bruit.
  */
  const aPointer = events.filter(
    (event) =>
      isPast(event, event.serverId) &&
      event.organizer.id === uid &&
      event.participants.some((membre) => membre.id !== uid),
  );
  const feuilles = await Promise.all(aPointer.map((event) => readSheetClosedAt(event.id)));

  return {
    // L'organisateur est aussi participant : sans cette exclusion, sa
    // propre carte apparaitrait dans les deux colonnes.
    joined: upcoming.filter((event) => event.organizer.id !== uid).sort(byDateAsc),
    organized: upcoming.filter((event) => event.organizer.id === uid).sort(byDateAsc),
    sheetsToClose: aPointer.filter((_, index) => !feuilles[index]).sort(byDateDesc),
    past,
    pastCount: past.length,
    trophyEventCount: ledger.count,
    trophySkipped: ledger.skipped,
  };
}

/**
 * Ce qui compte pour les trophees, parmi mes evenements passes.
 *
 * Dix evenements le meme jour ne valent qu'une journee : c'est le premier
 * de la journee qui la represente, les suivants sont ecartes mais jamais
 * caches — un compteur qui saute sans explication passe pour un bug.
 * L'autre garde-fou, l'evenement vecu seul, est applique en amont : il ne
 * figure meme pas dans `past`.
 */
function trophyLedger(past: SiroccoEvent[]): {
  count: number;
  skipped: Record<string, TrophySkip>;
} {
  const skipped: Record<string, TrophySkip> = {};
  const countedDays = new Set<string>();

  // La liste ne porte plus que des evenements ou l'on etait : la seule
  // raison qui reste d'en ecarter un est d'en avoir deja compte un ce
  // jour-la.
  for (const event of [...past].sort(byDateAsc)) {
    if (countedDays.has(event.date)) skipped[event.id] = 'same-day';
    else countedDays.add(event.date);
  }

  return { count: countedDays.size, skipped };
}

/**
 * Mes signets.
 * `bookmarkedEvents/{uid}` ne retient que l'identifiant de l'evenement,
 * pas son spot : on repasse donc par la recherche multi-serveurs.
 */
/**
 * Jusqu'a quand un membre reste joignable au titre de ses evenements.
 *
 * Le message prive est reserve aux amis, avec une exception : on peut
 * ecrire a qui organise un evenement encore a venir sur le serveur. Sans
 * elle, il faudrait devenir ami avec un inconnu pour lui demander l'heure
 * du rendez-vous — c'est-a-dire lui ouvrir tout son profil pour une
 * question de logistique.
 *
 * La fonction rend l'horaire de fin de son evenement le plus lointain, ou
 * `null` s'il n'en organise aucun a venir. C'est cette echeance qui est
 * rangee avec la conversation : la porte se referme d'elle-meme quand le
 * dernier evenement est passe.
 */
export async function organizerOpenUntil(
  serverId: string,
  organizerId: string,
): Promise<number | null> {
  const rows = await readList<EventRecord>(paths.events(serverId));

  const ends = rows
    .filter((event) => event.organizerId === organizerId && !isPast(event, serverId))
    .map((event) => finDe(event, fuseauDe(serverId)));

  return ends.length > 0 ? Math.max(...ends) : null;
}

export async function listBookmarked(): Promise<SiroccoEvent[]> {
  const uid = requireUid();
  const eventIds = await readKeys(paths.bookmarkedEvents(uid));
  if (eventIds.length === 0) return [];

  const sources = await findAnywhere(eventIds);
  const events = await composeMany(sources, uid);
  // Un evenement passe reste dans les signets : c'est un souvenir, pas
  // une invitation, mais il n'y a pas de raison de le faire disparaitre.
  return events.sort(byDateAsc);
}

/**
 * Les evenements que j'ai masques.
 *
 * Sans cet ecran, masquer etait un geste sans retour : l'evenement
 * quittait toutes mes listes et plus rien ne permettait de le retrouver.
 * Comme pour les signets, l'index ne retient que l'identifiant, pas le
 * spot : on repasse donc par la recherche multi-serveurs.
 */
export async function listHidden(): Promise<SiroccoEvent[]> {
  const uid = requireUid();
  const eventIds = await readOptionalKeys(paths.hiddenEvents(uid));
  if (eventIds.length === 0) return [];

  const sources = await findAnywhere(eventIds);
  const events = await composeMany(sources, uid);
  // Du plus proche au plus lointain, passes compris : on vient ici pour
  // retrouver quelque chose, pas pour lire un agenda.
  return events.sort(byDateAsc);
}

/**
 * Combien d'evenements la liste des masques montrera : ceux qu'on retrouve
 * encore. Un evenement supprime depuis laisse sa marque derriere lui.
 */
export async function countHidden(): Promise<number> {
  const eventIds = await readOptionalKeys(paths.hiddenEvents(requireUid()));
  return eventIds.length === 0 ? 0 : (await findAnywhere(eventIds)).length;
}

/* --- Ecritures ------------------------------------------------------ */

/**
 * Les lieux deja donnes aux evenements de ce spot, pour les reproposer.
 *
 * Sur un campus, aucune liste ne connait toutes les salles. Chaque lieu
 * ecrit par quelqu'un devient donc une proposition pour les suivants :
 * la liste se complete a l'usage, sans qu'on ait a la tenir. Un meme
 * lieu ecrit deux fois ne compte qu'une fois, et garde son point s'il en
 * a un quelque part.
 */
export async function lieuxDejaDonnes(
  serverId: string,
): Promise<{ nom: string; lat: number | null; lon: number | null }[]> {
  const records = await readList<EventRecord>(paths.events(serverId)).catch(() => []);
  const vus = new Map<string, { nom: string; lat: number | null; lon: number | null }>();
  for (const record of records) {
    /* « En ligne » n'est pas un lieu du campus. */
    if (record.enLigne) continue;
    const nom = (record.location ?? '').trim();
    if (!nom) continue;
    const cle = sansAccents(nom);
    const deja = vus.get(cle);
    const point = typeof record.lat === 'number' && typeof record.lon === 'number';
    if (!deja || (deja.lat === null && point)) {
      vus.set(cle, { nom, lat: point ? record.lat! : null, lon: point ? record.lon! : null });
    }
  }
  return [...vus.values()];
}

/** Cree un evenement et y inscrit son organisateur. */
export async function createEvent(
  serverId: string,
  input: CreateEventInput,
): Promise<SiroccoEvent> {
  const uid = requireUid();

  const title = input.title.trim();
  /*
    En ligne : ni adresse, ni point, ni territoire. Le lien est
    facultatif — une partie de console entre amis n'en a pas —, mais un
    lien donne doit mener quelque part.
  */
  const enLigne = input.enLigne === true && !input.spontane;
  const lienDonne = enLigne && Boolean(input.lien?.trim());
  const lien = lienDonne ? normaliserLeLien(input.lien ?? '') : null;
  const location = enLigne ? t.sorties.commun.enLigne : input.location.trim();
  if (!title) {
    throw new SiroccoDataError(t.sorties.erreurs.titre, 'invalid', 'title');
  }
  if (lienDonne && !lien) {
    throw new SiroccoDataError(t.sorties.erreurs.lien, 'invalid', 'lien');
  }
  if (!location) {
    throw new SiroccoDataError(t.sorties.erreurs.lieu, 'invalid', 'location');
  }
  /*
    Une adresse choisie, et non tapee.

    Le point ne vient que de l'annuaire : l'exiger, c'est exiger que le
    lieu existe et qu'on puisse s'y rendre. « Chez moi » ou « le parc »
    ne mene personne nulle part, et l'organisateur ne l'apprenait qu'en
    voyant venir des gens perdus.

    La verification est ici, dans la couche de donnees, et non dans
    l'ecran : c'est la seule porte par ou passent tous les evenements.
  */
  /*
    Sauf sur un campus : on y ecrit le nom du lieu tel qu'on le dit
    — « Salle 0.08 » —, et le placer sur le plan est facultatif. Un
    evenement sans point n'est simplement pas sur la carte.
  */
  if (
    !enLigne &&
    (input.lat === null || input.lon === null) &&
    (await readOptionalValue<boolean>(`${paths.server(serverId)}/campus`)) !== true
  ) {
    throw new SiroccoDataError(
      t.sorties.erreurs.adresse,
      'invalid',
      'location',
    );
  }
  if (!enLigne && !input.location.trim()) {
    throw new SiroccoDataError(t.sorties.erreurs.adresse, 'invalid', 'location');
  }
  /* Le territoire du spot : a Paris, la France. L'annuaire ne propose deja que lui. */
  if (!enLigne && input.lat !== null && input.lon !== null) {
    const limites = await readOptionalValue<LimitesDuSpot>(`${paths.server(serverId)}/limites`);
    if (!dansLesLimites({ lat: input.lat, lon: input.lon }, limites)) {
      throw new SiroccoDataError(t.sorties.erreurs.horsZone, 'invalid', 'location');
    }
  }
  if (!DATE_PATTERN.test(input.date)) {
    throw new SiroccoDataError(t.sorties.erreurs.dateInvalide, 'invalid', 'date');
  }
  if (input.date < today()) {
    throw new SiroccoDataError(t.sorties.erreurs.datePassee, 'invalid', 'date');
  }
  if (!input.startTime.trim()) {
    throw new SiroccoDataError(t.sorties.erreurs.heureDebut, 'invalid', 'startTime');
  }
  if (!input.endTime.trim()) {
    throw new SiroccoDataError(t.sorties.erreurs.heureFin, 'invalid', 'endTime');
  }
  // Une soiree de 22h a 2h est parfaitement normale : une fin plus petite
  // que le debut signifie le lendemain, pas une erreur de saisie. Seul un
  // horaire identique des deux cotes n'a aucun sens.
  if (minutesOf(input.endTime) === minutesOf(input.startTime)) {
    throw new SiroccoDataError(
      t.sorties.erreurs.finDifferente,
      'invalid',
      'endTime',
    );
  }
  /* Une sortie ou l'on ne sait pas quoi parler n'aide personne a venir. */
  const langues = versListe(versCarte(input.langues));
  if (langues.length === 0) {
    throw new SiroccoDataError(t.commun.uneLangue, 'invalid', 'langues');
  }
  if (langues.length > LANGUES_MAX_PAR_SORTIE) {
    throw new SiroccoDataError(t.sorties.creation.languesMax, 'invalid', 'langues');
  }
  if (
    !Number.isInteger(input.maxParticipants) ||
    input.maxParticipants < MIN_PARTICIPANTS ||
    input.maxParticipants > MAX_PARTICIPANTS
  ) {
    throw new SiroccoDataError(
      t.sorties.erreurs.nombreParticipants(MIN_PARTICIPANTS, MAX_PARTICIPANTS),
      'invalid',
      'maxParticipants',
    );
  }

  // On ne peut pas etre a deux endroits a la fois : organiser par-dessus
  // un creneau deja pris promettrait sa presence deux fois.
  await requireFreeSlot(
    { date: input.date, startTime: input.startTime, endTime: input.endTime },
    serverId,
  );

  // La fiche affiche "X evenements organises" : le compteur est fige a la
  // creation, une lecture du feed suffit a le calculer.
  const feed = await readList<EventRecord>(paths.events(serverId));
  const organizerEventCount = feed.filter((row) => row.organizerId === uid).length + 1;

  /* L'affiche part vers Storage avant l'ecriture, comme une photo de post. */
  const affiche = input.imageUrl ? await uploadIfDataUrl(input.imageUrl) : null;

  const eventId = newKey(paths.events(serverId));
  const record: EventRecord = {
    title,
    category: CATEGORIE_DE_REPLI[input.category],
    ...(CATEGORIE_DE_REPLI[input.category] !== input.category ? { categorie: input.category } : {}),
    // Le formulaire ne demande pas encore de sous-categorie : le libelle
    // de la categorie evite une fiche a moitie vide.
    subCategory: EVENT_CATEGORY_LABELS[input.category],
    organizerId: uid,
    organizerEventCount,
    date: input.date,
    startTime: input.startTime,
    endTime: input.endTime,
    location,
    lat: enLigne ? null : input.lat ?? null,
    lon: enLigne ? null : input.lon ?? null,
    description: input.description.trim(),
    langues: versCarte(langues),
    maxParticipants: input.maxParticipants,
    /* Une sortie spontanee ne valide personne : elle existe pour qu'on
       vienne, pas pour qu'on demande. */
    requiresValidation: input.spontane ? false : input.requiresValidation,
    /*
      L'affiche part vers Storage avant l'ecriture, comme une photo de
      post : l'ecran n'a jamais manipule qu'une data URL, ce qui lui donne
      son apercu immediat et laisse une creation abandonnee sans rien
      derriere elle.
    */
    ...(affiche ? { imageUrl: affiche } : {}),
    ...(input.seuil && input.seuil > 1 ? { seuil: input.seuil } : {}),
    ...(input.spontane ? { spontane: true } : {}),
    ...(enLigne ? { enLigne: true, ...(lien ? { lien } : {}) } : {}),
    ...(input.asso ? { asso: input.asso } : {}),
    createdAt: Date.now(),
  };

  // L'evenement, son index et la participation de son organisateur
  // partent dans la meme mise a jour : jamais d'evenement sans personne
  // dedans, ni d'evenement dont les regles ignorent l'organisateur.
  await updateValues({
    [paths.event(serverId, eventId)]: record,
    [paths.eventIndex(eventId)]: { serverId, organizerId: uid },
    [paths.eventParticipant(eventId, uid)]: 'confirmed',
  });

  const [event] = await compose([{ id: eventId, serverId, record }], [{ [uid]: 'confirmed' }], uid);
  return event;
}

/**
 * Demande a rejoindre un evenement.
 *
 * Une demande en attente ne prend aucune place : tant que l'organisateur
 * n'a pas tranche, le compteur de participants ne bouge pas. C'est la
 * seule lecture juste de "3 / 10" — sinon dix curieux rempliraient un
 * evenement que personne n'a valide.
 */
export async function joinEvent(event: SiroccoEvent): Promise<SiroccoEvent> {
  const uid = requireUid();
  refuseIfPast(event, t.sorties.erreurs.passe.inscrire);
  if (event.myStatus === 'pending' || event.myStatus === 'confirmed') return event;

  /*
    Un evenement a seuil dont l'heure est passee sans que le compte y
    soit est fini, meme si sa fin ne l'est pas. Sans cette porte, une
    inscription tardive le ferait repasser de « annule » a « confirme »
    sous les yeux de ceux qui avaient deja renonce.
  */
  if (event.statut === 'annule') {
    throw new SiroccoDataError(
      t.sorties.erreurs.annuleFaute,
      'invalid',
    );
  }

  /*
    Deux absences ferment l'inscription une semaine. La verification est
    ici plutot que dans l'ecran : c'est la seule porte par ou l'on entre
    dans un evenement, et une regle qui ne tient qu'a l'affichage ne tient
    pas.
  */
  const sanction = await readSanction();
  if (sanction.jusqua) {
    throw new SiroccoDataError(
      t.sorties.erreurs.suspendu(sanction.jusqua),
      'suspended',
    );
  }
  // Le creneau se verifie avant la demande, pas apres : l'organisateur
  // n'a aucun moyen de voir l'agenda de qui il valide.
  await requireFreeSlot(event, event.serverId, event.id);

  /*
    Complet ne veut plus dire ferme : on entre en liste d'attente.

    Un evenement plein refusait l'inscription, et c'etait tout — il
    fallait revenir voir de temps en temps si quelqu'un s'etait desiste.
    On prend donc les demandes quand meme, en attente, et la place qui
    se libere revient a celui qui a demande le premier.

    Ce qui suit est donc la meme file, remplie pour deux raisons
    differentes : parce que l'organisateur valide, ou parce qu'il n'y a
    plus de place. L'ecran des demandes les distingue, la base non : une
    demande en attente est une demande en attente.
  */
  const complet = event.participants.length >= event.maxParticipants;
  const status: ParticipationStatus =
    complet || event.requiresValidation ? 'pending' : 'confirmed';

  await updateValues({
    [paths.eventParticipant(event.id, uid)]: status,
    /* L'heure de la demande, qui tient la file dans l'ordre. Posee meme
       pour une entree directe : elle ne coute rien et une place peut se
       reprendre plus tard. */
    [paths.eventRequest(event.id, uid)]: Date.now(),
  });
  return getEvent(event.id, event.serverId);
}

/**
 * Accepte une demande de participation.
 *
 * Reserve a l'organisateur, cote application comme cote regles : celles-ci
 * le reconnaissent par `eventIndex`, d'ou le rattrapage pour les
 * evenements crees avant l'existence de cet index.
 */
export async function approveParticipant(
  event: SiroccoEvent,
  participantId: string,
): Promise<SiroccoEvent> {
  const uid = requireOrganizer(event);
  refuseIfPast(event, t.sorties.erreurs.passe.valider);
  if (event.participants.length >= event.maxParticipants) {
    throw new SiroccoDataError(
      t.sorties.erreurs.complet,
      'full',
    );
  }

  await ensureIndex(event, uid);
  const entree = await groupEntry(event.groupId, participantId);
  await updateValues({
    [paths.eventParticipant(event.id, participantId)]: 'confirmed',
    /* Il n'attend plus : sa date n'a plus de file a ordonner. */
    [paths.eventRequest(event.id, participantId)]: null,
    ...entree,
  });
  /* L'organisateur pose le mot : il est deja membre du fil, et la ligne
     nomme celui qui vient d'entrer. */
  if (event.groupId && Object.keys(entree).length > 0) {
    await noterArriveeDansLeGroupeDeSortie(event.groupId, participantId);
  }
  return getEvent(event.id, event.serverId);
}

/* --- La presence -------------------------------------------------- */

/**
 * Ce que la presence retient d'un passage a l'entree.
 *
 * L'heure sert a departager si l'organisateur se trompe, et le moyen dit
 * si le code a ete lu ou si la case a ete cochee a la main — les deux
 * comptent pareil, mais on veut pouvoir relire ce qui s'est passe.
 */
export interface AttendanceMark {
  at: number;
  via: 'qr' | 'manuel';
}

export type Attendance = Record<string, AttendanceMark>;

/**
 * Qui s'est presente.
 *
 * Lue a part de l'evenement plutot que posee dessus : une liste de vingt
 * cartes n'a que faire de la presence, et elle doublerait ses lectures
 * pour une information que seuls deux ecrans regardent.
 */
export async function readAttendance(eventId: string): Promise<Attendance> {
  return (await readOptionalValue<Attendance>(paths.eventAttendance(eventId))) ?? {};
}

/**
 * Marque quelqu'un present, ou revient dessus.
 *
 * Reserve a l'organisateur, ici comme dans les regles. La verification du
 * statut n'est pas un detail de confort : le code lu peut venir d'un autre
 * evenement, ou d'un membre retire depuis. On refuse plutot que de creer
 * une presence sans inscription.
 */
export async function markAttendance(
  event: SiroccoEvent,
  participantId: string,
  via: 'qr' | 'manuel',
): Promise<Attendance> {
  const uid = requireOrganizer(event);
  if (!event.participants.some((membre) => membre.id === participantId)) {
    throw new SiroccoDataError(
      t.sorties.erreurs.pasInscrite,
      'not-participant',
    );
  }

  await ensureIndex(event, uid);
  await writeValue(paths.eventAttendee(event.id, participantId), { at: Date.now(), via });
  return readAttendance(event.id);
}

/** Retire une presence : une erreur de scan ne doit pas etre definitive. */
export async function clearAttendance(
  event: SiroccoEvent,
  participantId: string,
): Promise<Attendance> {
  const uid = requireOrganizer(event);
  await ensureIndex(event, uid);
  await removeValue(paths.eventAttendee(event.id, participantId));
  return readAttendance(event.id);
}


/* --- La feuille de presence, les notes et les absences -------------- */

/**
 * Deux absences, et l'inscription se ferme une semaine.
 *
 * Le nombre et la duree sont ici plutot qu'eparpilles : c'est une regle
 * du produit, pas un detail d'ecran, et les deux ecrans qui l'annoncent
 * doivent dire la meme chose que celui qui l'applique.
 */
export const ABSENCES_AVANT_SANCTION = 2;
export const DUREE_SANCTION_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Ce que valent des absences, a un instant donne.
 *
 * Fonction pure, et volontairement : c'est la seule regle de ce fichier
 * qu'on peut eprouver sans base. Les absences se comptent dans l'ordre ;
 * a la deuxieme, la suspension part pour une semaine et le compteur
 * revient a zero — c'est la « reinitialisation ». Une absence tombee
 * pendant une suspension ne compte pas : on ne peut pas s'inscrire, donc
 * on ne peut pas manquer, et un evenement pointe en retard ne doit pas
 * punir deux fois.
 */
export function calculerSanction(absences: Absence[], maintenant = Date.now()): Sanction {
  const triees = [...absences].sort((a, b) => a.at - b.at);
  let compte = 0;
  let fin = 0;

  for (const absence of triees) {
    if (absence.at < fin) continue;
    compte += 1;
    if (compte >= ABSENCES_AVANT_SANCTION) {
      fin = absence.at + DUREE_SANCTION_MS;
      compte = 0;
    }
  }

  return { compte, jusqua: maintenant < fin ? fin : null };
}

/** Mes absences. Nul ne lit celles d'un autre : les regles s'y opposent. */
export async function readAbsences(): Promise<Absence[]> {
  const uid = requireUid();
  const brut = await readOptionalValue<Record<string, { at: number }>>(paths.absences(uid));
  return Object.entries(brut ?? {}).map(([eventId, valeur]) => ({ eventId, at: valeur?.at ?? 0 }));
}

/** Ou j'en suis : combien d'absences, et jusqu'a quand je suis suspendu. */
export async function readSanction(): Promise<Sanction> {
  return calculerSanction(await readAbsences());
}

/** Ce que rend l'arret d'une feuille de presence. */
export interface FeuilleArretee {
  closedAt: number;
  /** Combien de participants ont ete comptes absents. */
  absents: number;
}

/** La feuille de cet evenement a-t-elle ete arretee, et quand. */
export async function readSheetClosedAt(eventId: string): Promise<number | null> {
  const feuille = await readOptionalValue<{ closedAt: number }>(paths.eventSheet(eventId));
  return feuille?.closedAt ?? null;
}

/**
 * Arrete la feuille de presence.
 *
 * C'est ce geste, et lui seul, qui transforme « pas de marque de
 * presence » en absence : un organisateur qui oublie de pointer ne
 * penalise personne. Il ne se fait qu'une fois — les regles refusent de
 * l'ecraser — parce qu'une absence inscrite au casier de quelqu'un ne
 * doit pas dependre d'un second passage.
 *
 * L'organisateur ne se compte jamais absent de son propre evenement.
 */
export async function closeAttendanceSheet(event: SiroccoEvent): Promise<FeuilleArretee> {
  const uid = requireOrganizer(event);
  if (!event.isPast) {
    throw new SiroccoDataError(
      t.sorties.erreurs.feuillePasFinie,
      'not-past',
    );
  }
  if (await readSheetClosedAt(event.id)) {
    throw new SiroccoDataError(t.sorties.erreurs.feuilleValidee, 'already-closed');
  }

  await ensureIndex(event, uid);
  const presences = await readAttendance(event.id);
  const maintenant = Date.now();
  const absents = event.participants.filter(
    (membre) => membre.id !== uid && !presences[membre.id],
  );

  await updateValues({
    [paths.eventSheet(event.id)]: { closedAt: maintenant, organizerId: uid },
    ...Object.fromEntries(
      absents.map((membre) => [paths.absence(membre.id, event.id), { at: maintenant }]),
    ),
  });

  return { closedAt: maintenant, absents: absents.length };
}

/**
 * Noter l'organisateur.
 *
 * Reserve a qui a ete note present : c'est ce qui rend la note fiable, et
 * c'est pourquoi elle ne peut pas se donner avant que l'organisateur ait
 * pointe. Les regles le verifient, comme elles verifient que l'on note
 * bien l'organisateur de cet evenement-la et pas un autre.
 *
 * Elle ne se reprend pas : les regles refusent d'ecraser une note deja
 * posee. C'est l'ecran qui protege du geste de travers, en demandant
 * confirmation avant d'envoyer — une etoile touchee par megarde ne doit
 * pas suivre quelqu'un pour toujours.
 */
export async function rateOrganizer(event: SiroccoEvent, note: number): Promise<void> {
  const uid = requireUid();
  const arrondie = Math.round(note);
  if (arrondie < 1 || arrondie > 5) {
    throw new SiroccoDataError(t.sorties.erreurs.note, 'bad-rating');
  }
  await writeValue(paths.organizerRating(event.organizer.id, event.id, uid), {
    note: arrondie,
    at: Date.now(),
  });
}

/** La note que j'ai deja donnee sur cet evenement, s'il y en a une. */
export async function readMyRating(event: SiroccoEvent): Promise<number | null> {
  const uid = requireUid();
  const marque = await readOptionalValue<{ note: number }>(
    paths.organizerRating(event.organizer.id, event.id, uid),
  );
  return marque?.note ?? null;
}

/** La note moyenne d'un organisateur. `null` tant que personne ne l'a note. */
export async function readOrganizerRating(organizerId: string): Promise<OrganizerRating | null> {
  const [note] = await loadOrganizerRatings([organizerId]);
  return note ?? null;
}

/**
 * Les notes de plusieurs organisateurs, en une passe.
 *
 * Les memes organisateurs reviennent d'une carte a l'autre : on
 * dedoublonne avant de lire, comme `loadUsers` le fait pour les profils.
 */
async function loadOrganizerRatings(ids: string[]): Promise<(OrganizerRating | null)[]> {
  const uniques = [...new Set(ids)];
  const lues = await Promise.all(
    uniques.map(async (id) => {
      /*
        Deux niveaux : un evenement, puis ceux qui y etaient. Le tout tient
        en quelques dizaines de nombres, meme pour un organisateur assidu.
      */
      const brut = await readOptionalValue<Record<string, Record<string, { note: number }>>>(
        paths.organizerRatings(id),
      );
      const notes = Object.values(brut ?? {}).flatMap((parEvenement) =>
        Object.values(parEvenement ?? {}).map((marque) => marque?.note ?? 0),
      );
      const retenues = notes.filter((note) => note >= 1 && note <= 5);
      if (retenues.length === 0) return [id, null] as const;
      const somme = retenues.reduce((total, note) => total + note, 0);
      return [
        id,
        {
          moyenne: Math.round((somme / retenues.length) * 10) / 10,
          nombre: retenues.length,
        },
      ] as const;
    }),
  );

  const parId = new Map(lues);
  return ids.map((id) => parId.get(id) ?? null);
}

/* --- Modifier son evenement ---------------------------------------- */

/** Ce qu'un organisateur peut encore changer une fois l'evenement publie. */
export interface EventEdit {
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  /** Les langues qu'on y parlera, trois au plus. */
  langues: LangueParlee[];
}

/**
 * Le delai en deca duquel un evenement ne bouge plus.
 *
 * Dix heures : de quoi laisser chacun s'organiser la veille pour le
 * lendemain. Passe ce point, des gens ont pris un train, pose une soiree,
 * prevenu quelqu'un — deplacer l'evenement ne serait plus le modifier
 * mais le remplacer.
 */
export const EDITABLE_UNTIL_MS = 10 * 60 * 60 * 1000;

/** Combien de temps reste-t-il pour modifier ? Negatif : c'est trop tard. */
export function editableFor(event: SiroccoEvent): number {
  return debutDe(event, fuseauDe(event.serverId)) - EDITABLE_UNTIL_MS - Date.now();
}

/**
 * Change le titre, le jour, les horaires ou les langues.
 *
 * Le nombre de places n'en fait pas partie : le reduire mettrait dehors
 * des gens deja acceptes, l'augmenter changerait la nature de ce a quoi
 * les autres se sont inscrits.
 *
 * Le verrou des dix heures est pose ici, pas seulement dans l'ecran —
 * mais il n'est pas dans les regles : elles n'ont pas de quoi lire un
 * jour et un horaire ecrits en toutes lettres et en deduire un instant.
 * C'est donc une regle de l'application, tenue par l'application. Elle
 * protege les participants d'un organisateur distrait, pas d'un
 * organisateur decide a la contourner — et ce dernier ne peut de toute
 * facon toucher qu'a son propre evenement.
 */
export async function updateEvent(event: SiroccoEvent, edit: EventEdit): Promise<SiroccoEvent> {
  const uid = requireOrganizer(event);
  refuseIfPast(event, t.sorties.erreurs.passe.modifier);

  const titre = edit.title.trim();
  if (!titre) throw new SiroccoDataError(t.sorties.erreurs.titre, 'title');
  const langues = versListe(versCarte(edit.langues));
  if (langues.length === 0) throw new SiroccoDataError(t.commun.uneLangue, 'langues');
  if (langues.length > LANGUES_MAX_PAR_SORTIE) {
    throw new SiroccoDataError(t.sorties.creation.languesMax, 'langues');
  }
  if (minutesOf(edit.endTime) <= minutesOf(edit.startTime)) {
    throw new SiroccoDataError(t.sorties.erreurs.finApresDebut, 'endTime');
  }
  if (editableFor(event) <= 0) {
    throw new SiroccoDataError(
      t.sorties.erreurs.tropTard,
      'too-late',
    );
  }
  // Le nouveau creneau doit lui aussi tenir debout : rien ne sert de
  // deplacer un evenement vers un instant deja passe.
  if (debutDe(edit, fuseauDe(event.serverId)) < Date.now()) {
    throw new SiroccoDataError(t.sorties.erreurs.creneauPasse, 'date');
  }

  /*
    Deplacer un evenement, c'est prendre un creneau.

    La regle etait posee a la creation et a l'inscription, jamais au
    changement de date : on ne pouvait pas creer deux sorties a la meme
    heure, mais on pouvait en creer deux, puis ramener l'une sur l'autre.
    L'evenement deplace ne se compte pas lui-meme, sinon il se
    reprocherait le creneau qu'il occupe deja.
  */
  await requireFreeSlot(edit, event.serverId, event.id);

  await ensureIndex(event, uid);
  const base = paths.event(event.serverId, event.id);
  await updateValues({
    [`${base}/title`]: titre,
    [`${base}/date`]: edit.date,
    [`${base}/startTime`]: edit.startTime,
    [`${base}/endTime`]: edit.endTime,
    [`${base}/langues`]: versCarte(langues),
  });
  return getEvent(event.id, event.serverId);
}

/** Refuse une demande, ou retire un participant deja accepte. */
export async function removeParticipant(
  event: SiroccoEvent,
  participantId: string,
): Promise<SiroccoEvent> {
  const uid = requireOrganizer(event);
  refuseIfPast(event, t.sorties.erreurs.passe.figee);
  if (participantId === uid) {
    throw new SiroccoDataError(
      t.sorties.erreurs.organisateurReste,
      'organizer',
    );
  }

  await ensureIndex(event, uid);
  await updateValues({
    [paths.eventParticipant(event.id, participantId)]: null,
    [paths.eventRequest(event.id, participantId)]: null,
    /* Retire, et non parti : sans cette marque, la fonction qui annonce
       les departs previendrait l'organisateur de son propre geste. */
    [paths.eventRemoval(event.id, participantId)]: Date.now(),
    ...(await groupExit(event, participantId)),
  });
  return getEvent(event.id, event.serverId);
}

/**
 * Rattache une conversation de groupe a l'evenement.
 *
 * Le lien est ce qui distingue « le groupe de l'evenement » d'un groupe
 * ordinaire : sans lui, l'organisateur en recreerait un a chaque appui, et
 * retirer un participant le laisserait dans la discussion.
 */
export async function linkGroup(event: SiroccoEvent, conversationId: string): Promise<void> {
  requireOrganizer(event);
  await writeValue(`${paths.event(event.serverId, event.id)}/groupId`, conversationId);
}

/**
 * Les chemins qui font entrer ou sortir quelqu'un du groupe de l'evenement.
 *
 * Ils voyagent avec le changement de participation, dans la meme ecriture :
 * on ne veut pas de quelqu'un sorti de l'evenement mais reste dans son
 * groupe. Ils sont poses ici plutot que dans `messages`, qui importe deja
 * ce module — le detour inverse ferait un cycle.
 *
 * Une mise a jour groupee est tout ou rien. Si je ne suis plus moi-meme
 * dans le groupe, les regles refuseraient d'y toucher et emporteraient le
 * retrait de l'evenement avec elles : mieux vaut alors ne rien tenter.
 */
async function groupExit(
  event: SiroccoEvent,
  memberId: string,
): Promise<Record<string, string | null>> {
  const groupId = event.groupId;
  if (!(await canTouchGroup(groupId, memberId))) return {};

  /*
    Un groupe ne reste jamais sans administrateur. Celui qui sort pouvait
    l'etre — l'organisateur a pu lui passer la main — et personne n'est la
    pour designer un repreneur : le groupe revient alors a l'organisateur
    de l'evenement, a qui il appartient de toute facon.
  */
  const admin = await readOptionalValue<string>(`${paths.conversation(groupId!)}/adminId`);

  return {
    [paths.conversationMember(groupId!, memberId)]: null,
    [paths.userConversation(memberId, groupId!)]: null,
    ...(admin === memberId
      ? { [`${paths.conversation(groupId!)}/adminId`]: event.organizer.id }
      : {}),
  };
}

/** L'entree correspondante : un participant valide rejoint la discussion. */
async function groupEntry(groupId: string | null, memberId: string): Promise<Record<string, true>> {
  if (!(await puisJeEcrireDansLeGroupe(groupId))) return {};
  return {
    [paths.conversationMember(groupId as string, memberId)]: true,
    [paths.userConversation(memberId, groupId as string)]: true,
  };
}

/**
 * Ai-je le droit d'ecrire dans ce groupe ?
 *
 * La seule condition que posent les regles : en etre membre. Un droit
 * accorde sur la conversation s'etend a tout ce qu'elle contient, et l'on
 * ne peut rien y toucher sans en etre.
 */
async function puisJeEcrireDansLeGroupe(groupId: string | null): Promise<boolean> {
  if (!groupId) return false;
  return Boolean(await readOptionalValue(paths.conversationMember(groupId, requireUid())));
}

/**
 * Et celui qu'on veut en retirer y est-il ?
 *
 * Une condition de plus que pour l'entree, et elle manquait : on ne
 * retire personne d'un endroit ou il n'est pas. C'est ce qui cassait
 * « Annuler ma demande » — une demande en attente ne fait entrer dans
 * aucun groupe, on posait quand meme le retrait, la regle refusait ce
 * chemin-la, et comme une mise a jour groupee est tout ou rien,
 * l'annulation entiere echouait.
 *
 * Elle vaut pour la sortie seule. Posee aussi sur l'entree — ce qu'elle a
 * ete un temps — elle exigeait d'etre deja dans le groupe pour pouvoir y
 * entrer, et un participant valide n'y arrivait plus jamais.
 */
async function canTouchGroup(groupId: string | null, memberId: string): Promise<boolean> {
  if (!(await puisJeEcrireDansLeGroupe(groupId))) return false;
  if (memberId === requireUid()) return true;
  return Boolean(await readOptionalValue(paths.conversationMember(groupId as string, memberId)));
}

/**
 * Revenir dans le groupe d'une sortie a laquelle on est inscrit.
 *
 * On peut l'avoir quitte sans quitter la sortie : le groupe se rejoint
 * alors a nouveau, et c'est le seul chemin — on n'y entre pas sur
 * invitation. Les regles le disent aussi de leur cote, en verifiant
 * l'inscription plutot que l'appartenance.
 */
export async function rejoindreLeGroupe(event: SiroccoEvent): Promise<void> {
  const uid = requireUid();
  if (!event.groupId) {
    throw new SiroccoDataError(t.sorties.erreurs.sansGroupe, 'not-found');
  }
  await updateValues({
    [paths.conversationMember(event.groupId, uid)]: true,
    [paths.userConversation(uid, event.groupId)]: true,
  });
  /* Le mot apres le geste, ici : on n'est membre du fil qu'une fois
     entre, et la regle des messages ne s'ouvre qu'aux membres. */
  await noterArriveeDansLeGroupeDeSortie(event.groupId, uid);
}

/** Suis-je dans le groupe de cette sortie ? */
export async function suisJeDansLeGroupe(groupId: string | null): Promise<boolean> {
  return puisJeEcrireDansLeGroupe(groupId);
}

function requireOrganizer(event: SiroccoEvent): string {
  const uid = requireUid();
  if (event.organizer.id !== uid) {
    throw new SiroccoDataError(t.sorties.erreurs.seulOrganisateur, 'forbidden');
  }
  return uid;
}

/**
 * Ecrit l'index d'un evenement s'il manque.
 * Les evenements anterieurs a `eventIndex` n'en ont pas, et sans lui les
 * regles refuseraient a leur organisateur de valider qui que ce soit.
 */
async function ensureIndex(event: SiroccoEvent, uid: string): Promise<void> {
  if (await readOptionalValue(paths.eventIndex(event.id))) return;
  await writeValue(paths.eventIndex(event.id), {
    serverId: event.serverId,
    organizerId: uid,
  });
}

/**
 * Quitte un evenement.
 * L'organisateur passe par `handOverEvent` : se retirer sans successeur
 * laisserait un evenement que plus personne ne peut gerer.
 */
export async function leaveEvent(event: SiroccoEvent): Promise<SiroccoEvent> {
  const uid = requireUid();
  refuseIfPast(event, t.sorties.erreurs.passe.retirer);
  if (event.organizer.id === uid) {
    throw new SiroccoDataError(
      t.sorties.erreurs.organisateurPart,
      'organizer',
    );
  }

  const sortieDuGroupe = await groupExit(event, uid);
  /* Le mot avant le geste : la regle des messages ne s'ouvre qu'aux
     membres du fil, et l'on n'en est plus une fois sorti. */
  if (event.groupId && paths.conversationMember(event.groupId, uid) in sortieDuGroupe) {
    await noterSortieDuGroupeDeSortie(event.groupId);
  }

  /*
    Un depart laisse une marque, dans la meme ecriture que le depart.

    Effacer sa ligne ne dit pas qui l'a effacee : partir soi-meme, etre
    retire par l'organisateur, voir la sortie supprimee ecrivent tous le
    meme vide, et le declencheur n'a aucun moyen de les distinguer. La
    marque, elle, ne s'ecrit que d'ici. Seulement pour un participant
    confirme : annuler une demande n'est pas quitter une sortie.
  */
  const depart =
    event.myStatus === 'confirmed' ? { [paths.eventDeparture(event.id, uid)]: Date.now() } : {};

  await updateValues({
    [paths.eventParticipant(event.id, uid)]: null,
    /* Sa place dans la file s'en va avec lui : revenir, c'est redemander,
       et donc repasser derriere ceux qui attendaient deja. */
    [paths.eventRequest(event.id, uid)]: null,
    ...sortieDuGroupe,
    ...depart,
  });
  return getEvent(event.id, event.serverId);
}

/**
 * L'organisateur passe la main puis s'en va.
 *
 * Le successeur devient organisateur dans la fiche comme dans l'index —
 * ce dernier est ce sur quoi les regles s'appuient pour le laisser
 * valider les participants. Sans repreneur, il n'y a rien a transmettre :
 * l'evenement se supprime.
 */
export async function handOverEvent(
  event: SiroccoEvent,
  successorId: string,
): Promise<SiroccoEvent> {
  const uid = requireOrganizer(event);
  refuseIfPast(event, t.sorties.erreurs.passe.reprendre);
  if (successorId === uid) {
    throw new SiroccoDataError(t.sorties.erreurs.autreQuelquun, 'invalid');
  }
  const successor = [...event.participants, ...event.pendingParticipants].find(
    (member) => member.id === successorId,
  );
  if (!successor) {
    throw new SiroccoDataError(
      t.sorties.erreurs.neParticipePas,
      'not-a-participant',
    );
  }

  // Les regles lisent l'index *avant* la mise a jour : sans lui, elles ne
  // me reconnaitraient pas comme organisateur et refuseraient d'inscrire
  // le repreneur. Il doit donc exister d'abord, en une ecriture a part.
  await ensureIndex(event, uid);

  await updateValues({
    [`${paths.event(event.serverId, event.id)}/organizerId`]: successorId,
    [paths.eventIndex(event.id)]: { serverId: event.serverId, organizerId: successorId },
    // Le repreneur est confirme d'office : on ne laisse pas un organisateur
    // en attente de sa propre validation.
    [paths.eventParticipant(event.id, successorId)]: 'confirmed',
    [paths.eventParticipant(event.id, uid)]: null,
  });

  return getEvent(event.id, event.serverId);
}

/**
 * Supprime un evenement, avec ce qui n'a plus de sens sans lui.
 * Les signets et masquages des autres membres nous restent inaccessibles :
 * ils seront ignores a la lecture, comme pour un post efface.
 */
export async function deleteEvent(event: SiroccoEvent): Promise<void> {
  const uid = requireOrganizer(event);
  refuseIfPast(event, t.sorties.erreurs.passe.supprimer);
  // Meme raison que pour la passation : effacer le bloc des participants
  // passe par une regle qui cherche l'organisateur dans l'index.
  await ensureIndex(event, uid);

  /*
    Les inscrits restent en place : c'est la fonction qui les lit pour
    prevenir que la sortie n'aura pas lieu, puis qui les efface. Effaces
    ici, dans la meme ecriture, ils seraient deja partis quand elle
    arrive — voir `annoncerLaSuppression`.
  */
  await updateValues({
    [paths.event(event.serverId, event.id)]: null,
    [paths.eventIndex(event.id)]: null,
    [paths.eventAttendance(event.id)]: null,
    [paths.bookmarkedEvent(uid, event.id)]: null,
  });
}

/** Masque un evenement : il quitte mes listes, il reste chez les autres. */
export async function hideEvent(eventId: string): Promise<void> {
  await writeValue(paths.hiddenEvent(requireUid(), eventId), true);
}

/** Rend visible un evenement masque : de quoi revenir sur un geste. */
export async function unhideEvent(eventId: string): Promise<void> {
  await removeValue(paths.hiddenEvent(requireUid(), eventId));
}

/** Signalement d'un evenement, archive pour la moderation, et masquage. */
export async function reportEvent(
  event: Pick<SiroccoEvent, 'id' | 'serverId'>,
  reason: ReportReason,
  details = '',
): Promise<string> {
  const reporterId = requireUid();
  const reportId = newKey(paths.reports());

  await updateValues({
    [`${paths.reports()}/${reportId}`]: {
      type: 'event',
      eventId: event.id,
      serverId: event.serverId,
      reporterId,
      reason,
      details: details.trim(),
      createdAt: Date.now(),
    },
    // Meme regle que pour un post : signaler, c'est ne plus vouloir le voir.
    [paths.hiddenEvent(reporterId, event.id)]: true,
  });
  return reportId;
}

/**
 * Ajoute ou retire un signet.
 * Le signet n'appartient qu'a moi : inutile de relire l'evenement, l'etat
 * local suffit et le bouton reagit sans attente.
 */
export async function toggleBookmark(event: SiroccoEvent): Promise<SiroccoEvent> {
  const uid = requireUid();
  const path = paths.bookmarkedEvent(uid, event.id);

  if (event.bookmarked) await removeValue(path);
  else await writeValue(path, true);

  return { ...event, bookmarked: !event.bookmarked };
}

/* --- Composition ---------------------------------------------------- */

/** Lit les participations de plusieurs evenements, toutes en parallele. */
async function readRosters(sources: EventSource[]): Promise<Roster[]> {
  return Promise.all(
    sources.map((source) =>
      readValue<Record<string, ParticipationStatus>>(paths.eventParticipants(source.id)),
    ),
  );
}

async function composeMany(sources: EventSource[], uid: string): Promise<SiroccoEvent[]> {
  return compose(sources, await readRosters(sources), uid);
}

/**
 * Assemble des evenements affichables.
 * Les profils sont charges en un seul appel pour toute la liste : les
 * memes organisateurs et les memes participants reviennent d'une carte a
 * l'autre, et `loadUsers` dedoublonne. Les signets se lisent une fois,
 * puis se croisent en memoire.
 */
async function compose(
  sources: EventSource[],
  rosters: Roster[],
  uid: string,
): Promise<SiroccoEvent[]> {
  if (sources.length === 0) return [];

  const organisateurs = sources.map((source) => source.record.organizerId);
  const [users, bookmarkedIds, hiddenIds, notes, demandes] = await Promise.all([
    loadUsers(
      sources.flatMap((source, index) => [source.record.organizerId, ...keysOf(rosters[index])]),
    ),
    readKeys(paths.bookmarkedEvents(uid)),
    readOptionalKeys(paths.hiddenEvents(uid)),
    /* La note suit l'organisateur : elle se lit sur la carte d'un
       evenement comme sur sa fiche, sans second aller-retour. */
    loadOrganizerRatings(organisateurs),
    /* L'heure de chaque demande : c'est elle qui tient la file dans
       l'ordre. Son absence ne doit jamais emporter l'ecran — les
       demandes d'avant cette branche n'en ont pas. */
    Promise.all(
      sources.map((source) =>
        readOptionalValue<Record<string, number>>(paths.eventRequests(source.id)),
      ),
    ),
  ]);
  const bookmarks = new Set(bookmarkedIds);
  const masked = new Set(hiddenIds);

  return sources.map((source, index) =>
    toEvent(
      source,
      rosters[index],
      users,
      bookmarks.has(source.id),
      masked.has(source.id),
      uid,
      notes[index],
      demandes[index] ?? {},
    ),
  );
}

function toEvent(
  source: EventSource,
  roster: Roster,
  users: Map<string, PublicUser>,
  bookmarked: boolean,
  hidden: boolean,
  uid: string,
  organizerRating: OrganizerRating | null = null,
  /** Quand chacun a demande : voir `paths.eventRequests`. */
  demandeA: Record<string, number> = {},
): SiroccoEvent {
  const { record } = source;
  // Une place n'est prise qu'une fois la demande validee : les deux
  // listes sont donc separees des la lecture, et tout ce qui compte les
  // participants ne voit que les confirmes.
  const confirmedIds = idsWithStatus(roster, 'confirmed');
  /*
    La file, dans l'ordre ou elle s'est formee.

    Les cles de Firebase sortent par ordre alphabetique, ce qui n'a rien
    a voir avec l'ordre d'arrivee : sans ce tri, la premiere demande
    affichee serait celle du pseudo le plus proche de A. Une demande
    posee avant que cette date n'existe passe devant — elle est plus
    ancienne que toutes les autres, par construction.
  */
  const pendingIds = idsWithStatus(roster, 'pending').sort(
    (a, b) => (demandeA[a] ?? 0) - (demandeA[b] ?? 0),
  );

  return {
    id: source.id,
    serverId: source.serverId,
    title: record.title,
    category: categorieDe(record),
    subCategory: record.subCategory ?? '',
    organizer: users.get(record.organizerId) ?? unknownUser(record.organizerId),
    organizerEventCount: record.organizerEventCount ?? 1,
    organizerRating,
    date: record.date,
    startTime: record.startTime,
    endTime: record.endTime,
    /* « En ligne » dans la langue de qui lit, pas de qui l'a cree. */
    location: record.enLigne ? t.sorties.commun.enLigne : record.location,
    lat: record.enLigne ? null : record.lat ?? null,
    lon: record.enLigne ? null : record.lon ?? null,
    description: record.description ?? '',
    /* Avant ce champ, l'application ne parlait que francais. */
    langues: versListe(record.langues).length ? versListe(record.langues) : ['fr'],
    maxParticipants: record.maxParticipants,
    participants: confirmedIds.map((id) => users.get(id) ?? unknownUser(id)),
    pendingParticipants: pendingIds.map((id) => users.get(id) ?? unknownUser(id)),
    requiresValidation: Boolean(record.requiresValidation),
    myStatus: roster?.[uid] ?? 'none',
    bookmarked,
    hidden,
    isPast: isPast(record, source.serverId),
    imageUrl: record.imageUrl ?? null,
    seuil: record.seuil ?? null,
    spontane: Boolean(record.spontane),
    enLigne: Boolean(record.enLigne),
    /* Relu par la meme porte qu'a l'ecriture : un client modifie a pu y poser n'importe quoi. */
    lien: record.enLigne && record.lien ? normaliserLeLien(record.lien) : null,
    asso: record.asso ?? null,
    /*
      Le statut se deduit, il ne s'ecrit nulle part : rien a tenir a jour,
      rien a rattraper si une passe planifiee tombe. « Annule » veut dire
      que l'heure de debut est passee sans que le seuil soit atteint —
      personne ne s'est deplace pour rien.
    */
    statut: !record.seuil
      ? 'confirme'
      : confirmedIds.length >= record.seuil
        ? 'confirme'
        : debutDe(record, fuseauDe(source.serverId)) < Date.now()
          ? 'annule'
          : 'attente',
    groupId: record.groupId ?? null,
  };
}

/**
 * Les inscrits d'un statut donne.
 * Un ancien enregistrement peut porter n'importe quoi : on ne retient que
 * ce qui correspond exactement, faute de quoi un statut inconnu passerait
 * pour une place occupee.
 */
function idsWithStatus(roster: Roster, status: ParticipationStatus): string[] {
  return Object.entries(roster ?? {})
    .filter(([, value]) => value === status)
    .map(([id]) => id);
}

/* --- Recherche dans la base ----------------------------------------- */

async function readSource(serverId: string, eventId: string): Promise<EventSource | null> {
  /* Tolerante : cette lecture sert aussi a chercher dans tous les
     serveurs, dont ceux qui nous sont fermes. */
  const record = await readOptionalValue<EventRecord>(paths.event(serverId, eventId));
  return record ? { id: eventId, serverId, record } : null;
}

/**
 * Retrouve des evenements dont on ne connait pas le spot.
 *
 * L'index repond en une lecture ; ceux qui n'y figurent pas — crees avant
 * son introduction — sont cherches dans chaque ville, toutes les
 * combinaisons spot x evenement partant d'un coup.
 */
async function findAnywhere(eventIds: string[]): Promise<EventSource[]> {
  const indexed = await Promise.all(
    eventIds.map((eventId) => readOptionalValue<{ serverId: string }>(paths.eventIndex(eventId))),
  );
  const known = await Promise.all(
    eventIds.map((eventId, position) =>
      indexed[position]?.serverId ? readSource(indexed[position]!.serverId, eventId) : null,
    ),
  );

  const missing = eventIds.filter((_, position) => !known[position]);
  let swept: (EventSource | null)[] = [];
  if (missing.length > 0) {
    const servers = await readList<Record<string, unknown>>(paths.servers());
    swept = await Promise.all(
      missing.flatMap((eventId) => servers.map((server) => readSource(server.id, eventId))),
    );
  }

  return [...known, ...swept].filter((source): source is EventSource => source !== null);
}

/**
 * Les spots ou j'ai pu creer ou rejoindre quelque chose.
 * Le profil garde une entree par destination visitee : c'est le seul
 * moyen de ne pas balayer la base entiere. Sans destination enregistree,
 * on retombe sur le catalogue complet.
 */
async function myServerIds(uid: string): Promise<string[]> {
  const record = await readValue<UserRecord>(paths.user(uid));
  const ids = new Set(keysOf(record?.destinations));
  if (record?.currentServerId) ids.add(record.currentServerId);

  if (ids.size === 0) {
    const servers = await readList<Record<string, unknown>>(paths.servers());
    for (const server of servers) ids.add(server.id);
  }
  return [...ids];
}

/* --- Dates, tri et recherche ---------------------------------------- */

/** Aujourd'hui au format "AAAA-MM-JJ", dans le fuseau du navigateur. */
function today(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Ce qu'il faut d'un evenement pour savoir s'il est termine. */
type Horaires = Pick<SiroccoEvent, 'date' | 'startTime' | 'endTime'>;

/**
 * Un evenement est passe une fois son horaire de fin franchi, **a l'heure
 * du spot**.
 *
 * Deux corrections successives. On comparait d'abord la seule date : un
 * match de 17h a 18h restait « a venir » jusqu'a minuit. Puis l'instant
 * se fabriquait dans le fuseau de l'appareil : un organisateur a Paris et
 * un participant a Marrakech ne basculaient pas au meme moment, et
 * personne ne pouvait s'en apercevoir depuis une seule ville. Le calcul
 * vit desormais dans `utils/spotTime`, et lui seul.
 */
function isPast(event: Horaires, serverId: string): boolean {
  return estPasse(event, fuseauDe(serverId));
}

/**
 * Refuse toute action sur un evenement termine.
 *
 * Le verrou est pose ici et non dans les ecrans : un evenement passe est
 * une trace, et les trophees des participants s'appuient dessus. En
 * retirer quelqu'un ou l'effacer reecrirait le passe de gens qui y
 * etaient. Les ecrans cachent les boutons ; cette fonction garantit que
 * les cacher suffit.
 */
function refuseIfPast(event: SiroccoEvent, what: string): void {
  if (isPast(event, event.serverId)) {
    throw new SiroccoDataError(t.sorties.erreurs.estPasse(what), 'event-past');
  }
}

/**
 * Refuse un creneau deja occupe.
 *
 * On ne peut pas etre a deux endroits en meme temps, et un evenement ou
 * l'on ne vient pas coute une place a quelqu'un d'autre. La comparaison
 * porte sur mes engagements a venir : ceux que j'organise, et ceux ou ma
 * participation est confirmee. Une demande encore en attente n'est pas un
 * engagement, mais elle occupe quand meme le creneau — sans quoi
 * l'organisateur validerait a l'aveugle un conflit qu'il ne voit pas.
 *
 * Chaque creneau se lit dans le fuseau de **son** spot : comparer deux
 * villes revient a comparer deux instants, pas deux heures murales. Un
 * membre qui organise a Berlin et participe a Marrakech le meme jour n'a
 * pas forcement de conflit. Bornes ouvertes : enchainer un evenement qui
 * finit a 18h avec un autre qui commence a 18h est courant.
 */
async function requireFreeSlot(
  slot: Horaires,
  serverId: string,
  exceptEventId?: string,
): Promise<void> {
  const { joined, organized } = await listMine();
  const ici = fuseauDe(serverId);
  const debutSlot = debutDe(slot, ici);
  const finSlot = finDe(slot, ici);

  const clash = [...organized, ...joined].find((event) => {
    if (event.id === exceptEventId) return false;
    const la = fuseauDe(event.serverId);
    return debutDe(event, la) < finSlot && debutSlot < finDe(event, la);
  });

  if (clash) {
    throw new SiroccoDataError(
      t.sorties.erreurs.creneauPris(clash.title),
      'slot-taken',
      'startTime',
    );
  }
}

function byDateAsc(a: SiroccoEvent, b: SiroccoEvent): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  return minutesOf(a.startTime) - minutesOf(b.startTime);
}

function byDateDesc(a: SiroccoEvent, b: SiroccoEvent): number {
  return byDateAsc(b, a);
}

/**
 * Un horaire en minutes depuis minuit, pour trier la journee.
 * Deux ecritures cohabitent — "17h30" pour les evenements d'origine,
 * "17:30" pour ceux saisis depuis le formulaire — et comparer les chaines
 * telles quelles placerait "9h" apres "17h".
 */
function minutesOf(time: string): number {
  const match = /^(\d{1,2})\s*[h:]?\s*(\d{2})?/.exec((time ?? '').trim());
  if (!match) return 0;
  return Number(match[1]) * 60 + Number(match[2] ?? 0);
}

/** Minuscules et sans accents : "Théâtre" doit repondre a "theatre". */
function fold(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** La recherche porte sur ce qu'on lit d'une carte : titre, lieu, sous-categorie. */
function matches(record: EventRecord, needle: string): boolean {
  if (!needle) return true;
  const haystack = fold(`${record.title} ${record.location} ${record.subCategory ?? ''}`);
  return haystack.includes(needle);
}
